import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { testModel } from "../../services/models";
import { buildChatPrompt, readReasoning } from "../../utils/modelRegistry";

// A live chat against a registered model, so its key/endpoint can be verified by talking to it.
// The registry's `/test` endpoint takes a single prompt, so each turn sends the transcript so
// far and shows the model's reply.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000000000;
  padding: 20px;
  box-sizing: border-box;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  background-color: var(--dialog-bg);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--main-border-color);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--main-font-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #a0a6b1;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Messages = styled.div`
  flex: 1;
  min-height: 240px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: var(--main-bg);
`;

const Bubble = styled.div`
  align-self: ${(props) => (props.$role === "user" ? "flex-end" : "flex-start")};
  max-width: 82%;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(props) => (props.$role === "error" ? "#c0392b" : "var(--main-font-color)")};
  background-color: ${(props) =>
    props.$role === "user"
      ? "var(--button-bg)"
      : props.$role === "error"
        ? "rgba(192, 57, 43, 0.12)"
        : "var(--content-item-bg)"};
  color: ${(props) => (props.$role === "user" ? "var(--button-font-color)" : undefined)};
  border: 1px solid ${(props) => (props.$role === "assistant" ? "var(--main-border-color)" : "transparent")};
`;

/* Reasoning is a muted, italic quote beside the answer — deliberately a different voice. */
const Reasoning = styled.details`
  align-self: flex-start;
  max-width: 78%;
  margin: 2px 0 6px 10px;
  padding: 2px 0 2px 10px;
  font-size: 12px;
  line-height: 1.55;
  font-style: italic;
  color: #8b93a1;
  background: transparent;
  border-left: 3px solid var(--main-border-color);

  summary {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-style: normal;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8b93a1;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::after {
    content: "▾";
    font-size: 9px;
    transition: transform 0.12s ease;
  }

  pre {
    margin: 5px 0 0;
    max-height: 220px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-style: italic;
  }
`;

const Empty = styled.div`
  margin: auto;
  font-size: 13px;
  color: #a0a6b1;
  text-align: center;
  line-height: 1.5;
`;

const Composer = styled.form`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--main-border-color);
`;

const Input = styled.input`
  flex: 1;
  padding: 9px 11px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
`;

const Send = styled.button`
  padding: 9px 16px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background-color: var(--button-bg);
  color: var(--button-font-color);
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 12px;
  font-size: 11px;
  color: #a0a6b1;
`;

const Clear = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  font-size: 11px;
  border: 1px solid var(--main-border-color);
  border-radius: 5px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

/**
 * Chat with a registered model.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {object|null} props.model The model record (`public_id`/`name`, provider, deployment).
 * @param {Function} props.onClose
 */
export const ModelChatDialog = ({ show = false, model = null, onClose = () => { } }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const idOrName = model?.public_id ?? model?.name ?? null;
  const modelLabel = model?.name ?? model?.public_id ?? "model";
  const detail = [model?.provider, model?.deployment].filter(Boolean).join(" · ");

  // Start fresh whenever the dialog opens (or targets a different model).
  useEffect(() => {
    if (show) {
      setMessages([]);
      setInput("");
      setSending(false);
    }
  }, [show, idOrName]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (event) => {
    event.preventDefault();

    const text = input.trim();
    if (!text || sending || !idOrName) return;

    const prompt = buildChatPrompt(messages, text);

    setMessages((current) => [...current, { role: "user", content: text }]);
    setInput("");
    setSending(true);

    const result = await testModel(idOrName, prompt);

    setSending(false);

    if (result?.success) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: result.response ? result.response : "(empty response)",
          reasoning: readReasoning(result),
        },
      ]);
    } else {
      setMessages((current) => [
        ...current,
        { role: "error", content: result?.message || "The model did not respond." },
      ]);
    }
  };

  if (!show) return null;

  return (
    <Overlay>
      <Panel>
        <Header>
          <Icon icon="mdi:message-text-outline" style={{ fontSize: "20px", color: "var(--main-font-color)" }} />
          <HeaderText>
            <Title>Chat with {modelLabel}</Title>
            <Subtitle>{detail ? `Live test · ${detail}` : "Live test using the stored key"}</Subtitle>
          </HeaderText>
          <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
        </Header>

        <Messages ref={listRef}>
          {messages.length === 0 ? (
            <Empty>
              Send a message to test this model.
              <br />
              Each turn includes the conversation so far.
            </Empty>
          ) : (
            messages.map((message, index) => (
              <React.Fragment key={index}>
                <Bubble $role={message.role}>{message.content}</Bubble>
                {message.reasoning ? (
                  <Reasoning open>
                    <summary>
                      <Icon icon="mdi:head-cog-outline" style={{ fontSize: "13px" }} />
                      Reasoning
                    </summary>
                    <pre>{message.reasoning}</pre>
                  </Reasoning>
                ) : null}
              </React.Fragment>
            ))
          )}
          {sending ? <Bubble $role="assistant">…</Bubble> : null}
        </Messages>

        <Composer onSubmit={send}>
          <Input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={sending ? "Waiting for the model…" : "Type a message and press Enter"}
            disabled={sending || !idOrName}
            autoFocus
          />
          <Send type="submit" disabled={sending || !input.trim() || !idOrName}>
            {sending ? "Sending…" : "Send"}
          </Send>
        </Composer>

        <Footer>
          Uses the saved credentials for this model.
          <Clear type="button" onClick={() => setMessages([])} disabled={messages.length === 0}>
            Clear chat
          </Clear>
        </Footer>
      </Panel>
    </Overlay>
  );
};

export default ModelChatDialog;
